import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {PushPublicKeyResponse, PushSubscribeRequest} from '../../../models/generated-models';

/**
 * Browser Web Push opt-in. `enable()` (triggered from the header bell) registers the push service
 * worker, asks for Notification permission, subscribes via the VAPID public key, and posts the
 * subscription to the backend. A minimal custom SW at /ui/sw-push.js handles the incoming push.
 */
@Injectable({providedIn: 'root'})
export class PushNotificationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.serverUrl;

  /** Whether this browser currently holds a push subscription. */
  readonly subscribed = signal(false);

  isSupported(): boolean {
    return typeof navigator !== 'undefined'
      && 'serviceWorker' in navigator
      && typeof window !== 'undefined'
      && 'PushManager' in window
      && 'Notification' in window;
  }

  get permission(): NotificationPermission {
    return this.isSupported() ? Notification.permission : 'denied';
  }

  /** Reflect the current subscription state into the `subscribed` signal (call on app/bell init). */
  async refresh(): Promise<void> {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      this.subscribed.set(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration('/ui/');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      this.subscribed.set(!!sub);
    } catch {
      this.subscribed.set(false);
    }
  }

  /** Request permission + subscribe. Returns true on success. Safe to call repeatedly (idempotent). */
  async enable(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    try {
      const registration = await navigator.serviceWorker.register('/ui/sw-push.js', {scope: '/ui/'});
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.subscribed.set(false);
        return false;
      }
      const ready = await navigator.serviceWorker.ready.catch(() => registration);
      let subscription = await ready.pushManager.getSubscription();
      if (!subscription) {
        const key = await firstValueFrom(
          this.http.get<PushPublicKeyResponse>(`${this.baseUrl}/api/push/public-key`));
        if (!key?.publicKey) {
          return false;
        }
        subscription = await ready.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(key.publicKey) as BufferSource,
        });
      }
      const json = subscription.toJSON();
      const payload: PushSubscribeRequest = {
        endpoint: json.endpoint,
        p256dh: json.keys?.['p256dh'],
        auth: json.keys?.['auth'],
      };
      await firstValueFrom(this.http.post<void>(`${this.baseUrl}/api/push/subscribe`, payload));
      this.subscribed.set(true);
      return true;
    } catch {
      this.subscribed.set(false);
      return false;
    }
  }

  async disable(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration('/ui/');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await firstValueFrom(
          this.http.delete<void>(`${this.baseUrl}/api/push/subscribe`, {params: {endpoint}}));
      }
    } catch {
      // best-effort
    } finally {
      this.subscribed.set(false);
    }
  }

  private urlBase64ToUint8Array(base64Url: string): Uint8Array {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      output[i] = raw.charCodeAt(i);
    }
    return output;
  }
}
